import type { MemberProfileResponse, SubscriptionEvent } from '../../types/member.js';

/** @graph-connects none */
const EVENT_TYPE_STYLES: Record<string, { bg: string; text: string }> = {
  アカウント作成: { bg: 'var(--color-accent-bg)', text: 'var(--color-accent-primary)' },
  サブスク登録: { bg: 'var(--color-success-bg)', text: 'var(--color-success)' },
  解約申請: { bg: 'var(--color-warning-bg)', text: 'var(--color-warning)' },
  キャンセル確定: { bg: 'var(--color-error-bg)', text: 'var(--color-error)' },
  サービス提供開始: { bg: 'var(--color-success-bg)', text: 'var(--color-success)' },
};

/** @graph-connects none */
const SUBSCRIPTION_STATUS_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  active: { bg: 'var(--color-success-bg)', text: 'var(--color-success)', label: '有効' },
  waiting: { bg: 'var(--color-warning-bg)', text: 'var(--color-warning)', label: '空き待ち' },
  // 「解約申請」イベントと同じ概念なので同色にする。色の正本は lib/member-constants.ts の
  // STATUS_STYLES（cancel_requested = warning）で、確定済みの canceled のみ error 系に落とす。
  cancel_requested: {
    bg: 'var(--color-warning-bg)',
    text: 'var(--color-warning)',
    label: '解約申請中',
  },
  canceled: { bg: 'var(--color-bg-elevated)', text: 'var(--color-text-muted)', label: '解約済み' },
};

interface SubscriptionTabProps {
  profile: MemberProfileResponse;
}

/** @graph-connects none */
export function SubscriptionTab({ profile }: SubscriptionTabProps) {
  const events = profile.subscriptionEvents;

  return (
    <div className="overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)]">
      <div className="border-b border-[var(--color-border)] px-4 py-3 text-[13px] font-semibold text-[var(--color-text-primary)]">
        サブスクリプション・プラン イベント時系列
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--color-border)] bg-[var(--color-bg-elevated)]">
              <th className="px-3.5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-secondary)]">
                日時
              </th>
              <th className="px-3.5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-secondary)]">
                イベント
              </th>
              <th className="px-3.5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-secondary)]">
                プラン
              </th>
              <th className="px-3.5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-secondary)]">
                料金
              </th>
              <th className="px-3.5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-secondary)]">
                備考
              </th>
            </tr>
          </thead>
          <tbody>
            {events.map((ev, i) => (
              <EventRow key={i} event={ev} />
            ))}
          </tbody>
        </table>
      </div>
      <div className="border-t border-[var(--color-border)] px-4 py-2.5 text-[11px] text-[var(--color-text-muted)]">
        現在のステータス: <StatusBadge status={profile.subscriptionStatus} /> | 現在のプラン:{' '}
        {profile.currentPlanName ?? '-'}
        {profile.currentPlanPrice !== null &&
          ` (¥${String(profile.currentPlanPrice).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}/月)`}
      </div>
    </div>
  );
}

/** @graph-connects none */
function EventRow({ event }: { event: SubscriptionEvent }) {
  const style = EVENT_TYPE_STYLES[event.eventType] ?? {
    bg: 'var(--color-bg-elevated)',
    text: 'var(--color-text-muted)',
  };

  return (
    <tr className="border-b border-[var(--color-border)]">
      <td className="whitespace-nowrap px-3.5 py-2.5 font-mono text-[12px]">
        {formatJst(event.occurredAt)}
      </td>
      <td className="whitespace-nowrap px-3.5 py-2.5">
        <span
          className="inline-block rounded-full px-2 py-0.5 text-[11px] font-medium"
          style={{ background: style.bg, color: style.text }}
        >
          {event.eventType}
        </span>
      </td>
      <td className="px-3.5 py-2.5 text-[13px]">
        {event.planName ?? <span className="text-[var(--color-text-muted)]">-</span>}
      </td>
      <td className="whitespace-nowrap px-3.5 py-2.5 font-mono text-[12px]">
        {event.planPrice !== null
          ? `¥${String(event.planPrice).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`
          : '-'}
      </td>
      <td className="px-3.5 py-2.5 text-[12px] text-[var(--color-text-secondary)]">
        {event.note ?? ''}
      </td>
    </tr>
  );
}

/** @graph-connects none */
function StatusBadge({ status }: { status: string | null }) {
  const sub = SUBSCRIPTION_STATUS_STYLES[status ?? ''];
  if (!sub) return <span>{status ?? '-'}</span>;
  return (
    <span
      className="inline-block rounded-full px-2 py-0.5 text-[11px] font-medium"
      style={{ background: sub.bg, color: sub.text }}
    >
      {sub.label}
    </span>
  );
}

/** @graph-connects none */
function formatJst(isoDate: string): string {
  const _d = new Date(isoDate);
  if (Number.isNaN(_d.getTime())) return isoDate;
  return _d.toLocaleString('ja-JP', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}
